import "./Register.scss";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonText,
  isPlatform,
} from "@ionic/react";
import { Plugins } from "@capacitor/core";
import clsx from "clsx";
import { registerUser, setAlias } from "../../api/user";
// import {
//   getUserByIdentity,
//   recoverIdentity,
//   setAlias,
//   setEmail,
//   setUsername,
//   updateUser,
// } from "../../api/user";
import * as jwtDecode from "jwt-decode";
import { useTracked } from "../../state";
import AlertBox from "../../components/AlertBox";
import CheckEmailModal from "../../components/CheckEmailModal";
import CreatingIdentity from "../../components/CreatingIdentity";
import LandingHeader from "../../components/LandingHeader";
import LandingFooter from "../../components/LandingFooter";
import { useHistory } from "react-router-dom";

const { Keyboard } = Plugins;

const Register: React.FC = () => {
  const history = useHistory();
  const createdDid = useRef<any>(null);
  const [state, dispatch] = useTracked();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenKeyboard, setIsOpenKeyboard] = useState<boolean>(false);
  // const [loadingSpinnerMessage, setLoadingSpinnerMessage] = useState("");
  const [isOpenAlertError, setIsOpenAlertError] = useState(false);
  const [alertErrorMessage, setAlertErrorMessage] = useState("");
  const [isOpenCheckEmail, setIsOpenCheckEmail] = useState(false);
  const [isOpenCreatingIdentity, setIsOpenCreatingIdentity] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [progressValue, setProgressValue] = useState<any>(0);

  const [username, setUsername] = useState<string>("");
  const [usernameError, setUsernameError] = useState("");

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState("");

  const [targetAlias, setTargetAlias] = useState("");
  const [isAliasCreated, setIsAliasCreated] = useState(false);

  const regexUsername = /^[a-zA-Z0-9]*$/;
  const isEmail: RegExp =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const isUnmounted = useRef<boolean>(false);
  const willCreateAlias = useRef<boolean>(true);

  useLayoutEffect(() => {
    createdDid.current = null;
  }, []);

  useEffect(() => {
    if (isPlatform("cordova")) {
      Keyboard.addListener("keyboardDidShow", () => {
        setIsOpenKeyboard(true);
      });
      Keyboard.addListener("keyboardWillHide", () => {
        setIsOpenKeyboard(false);
      });
    }
  }, []);

  useEffect(() => {
    if (!state.hasOnboarding) {
      history.replace("/onboarding");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.hasOnboarding]);

  useEffect(() => {
    if (state.isCreatingAliasToken && targetAlias && willCreateAlias.current) {
      willCreateAlias.current = false;
      dispatch({ type: "setIsCreatingAliasToken", isCreatingAliasToken: null });
      createAlias(state.isCreatingAliasToken, targetAlias);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isCreatingAliasToken, targetAlias]);

  const handleUsernameChange = (e: any) => {
    const { value } = e.target;
    setUsernameError("");
    setUsername(value);
  };

  const handleEmailChange = (e: any) => {
    const { value } = e.target;
    setEmailError("");
    setEmail(value);
  };

  const createAlias = async (token: string, alias: string) => {
    try {
      setIsOpenCreatingIdentity(true);
      setIsOpenCheckEmail(false);

      setLoadingText("Creating your secure identity");
      setProgressValue(0.25);
      await mockDelay(1000);

      const { sub } = jwtDecode(token);
      const resp: any = await setAlias(sub.toLowerCase(), alias, { appId: 1 });

      if (resp?.status === 200) {
        setLoadingText("Storing your identity on-chain");
        setProgressValue(0.5);
        await mockDelay(2500);

        setLoadingText("Populating credentials");
        setProgressValue(0.75);
        await mockDelay(2500);

        setLoadingText("Finalizing...");
        setProgressValue(1);
        await mockDelay(2500);

        setIsAliasCreated(true);
        await mockDelay(2500);

        publishLogin(sub.toLowerCase(), token);
      } else {
        setIsOpenCreatingIdentity(false);
        setAlertErrorMessage(resp?.message || "unable to create new DID");
        setIsOpenAlertError(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const mockDelay = async (ms: number) => {
    return new Promise(async (resolve, reject) => {
      try {
        setTimeout(() => {
          resolve(true);
        }, ms);
      } catch (e) {
        reject(e);
      }
    });
  };

  const publishLogin = async (identity: string, token: string) => {
    if (state.mqm) {
      await state.mqm.pubnub.publish({
        channel: identity.toLowerCase(),
        meta: {
          type: "login",
        },
        message: { identity, token, appId: process.env.REACT_APP_APP_ID },
      });
    }
  };

  const handleSubmit = async () => {
    let valid = true;

    if (username.length < 4) {
      valid = false;
      setUsernameError("Username must be atleast 4 characters long.");
    } else if (!regexUsername.test(username)) {
      valid = false;
      setUsernameError(
        `The following characters cannot be used: (&, =, _, ', -, +, ,, <, >, !, @, #, $, %, ^, &, * ).`
      );
    }

    if (!email) {
      valid = false;
      setEmailError("Email address is required.");
      return;
    } else if (!isEmail.test(email)) {
      valid = false;
      setEmailError("Please enter a valid email address.");
      return;
    }
    //  else if (email.length > 32) {
    //   valid = false;
    //   setEmailError("Email must be less than 32 characters long.");
    //   return;
    // }

    // if (email.indexOf("_") !== -1) {
    //   valid = false;
    //   setEmailError("Underscores are not allowed");
    //   return;
    // }

    if (valid) {
      setIsLoading(true);

      try {
        const res = await registerUser({
          email,
          username,
        });

        if (res && res.status === 200 && res.token) {
          const { sub: identity } = jwtDecode(res.token);

          if (identity) {
            localStorage.setItem("isFromRecoverFlow", JSON.stringify(false));
            setIsLoading(false);
            setTargetAlias(email);
            setIsOpenCheckEmail(true);
            await mockDelay(500);
            dispatch({ identity, type: "setIdentity" });
          }
        } else {
          setAlertErrorMessage(res?.message || "");
          setIsOpenAlertError(true);
        }
      } catch (e) {
        console.log(e);
      }
      setIsLoading(false);
    }
  };

  return (
    <IonPage
      className={clsx("register-page", { "on-keyboard": isOpenKeyboard })}
    >
      <IonContent className="register-content" hidden={isOpenCreatingIdentity}>
        <div className="register-container">
          <LandingHeader />
          <div className="content-body">
            <IonList>
              <IonItem lines="none">
                <IonLabel position="stacked" style={{ marginLeft: "5px" }}>
                  Username
                </IonLabel>
                <IonInput
                  name="username"
                  type="text"
                  inputMode="text"
                  placeholder="Enter username here"
                  value={username}
                  className={clsx({ "has-error": !!usernameError })}
                  maxlength={48}
                  onIonChange={handleUsernameChange}
                />
                {usernameError && (
                  <div className="error-text">{usernameError}</div>
                )}
              </IonItem>

              <IonItem lines="none">
                <IonLabel position="stacked" style={{ marginLeft: "5px" }}>
                  Email
                </IonLabel>

                <IonInput
                  name="email"
                  type="email"
                  inputMode="email"
                  placeholder="Enter email here"
                  value={email}
                  className={clsx({ "has-error": !!emailError })}
                  maxlength={144}
                  onIonChange={handleEmailChange}
                />
                {emailError && <div className="error-text">{emailError}</div>}
              </IonItem>
            </IonList>

            <IonButton
              color={!username || !email ? "light" : "danger"}
              expand="full"
              shape="round"
              className={clsx("register-btn", {
                disabled: !username || !email,
              })}
              disabled={!username || !email}
              onClick={() => {
                handleSubmit();
              }}
            >
              Create Identity
            </IonButton>

            <div className="btn-content">
              <IonText className="or-identity-label">Or</IonText>
            </div>

            <IonText className="recover-label">Recover your identity</IonText>

            <IonButton
              expand="full"
              fill="outline"
              shape="round"
              className="recover-btn"
              routerLink="/"
              routerDirection="none"
            >
              Recover
            </IonButton>
          </div>
        </div>
      </IonContent>
      <LandingFooter hidden={isOpenCreatingIdentity} />

      <CreatingIdentity
        isShow={isOpenCreatingIdentity}
        loadingText={loadingText}
        progressValue={progressValue}
        isAccountCreated={isAliasCreated}
      />

      <CheckEmailModal isOpen={isOpenCheckEmail} />

      <IonLoading
        cssClass="custom-loading-spinner"
        isOpen={isLoading}
        // message={loadingSpinnerMessage ? loadingSpinnerMessage : ""}
        onDidDismiss={() => {
          if (!isUnmounted.current) {
            setIsLoading(false);
          }
        }}
      />

      {isOpenAlertError && (
        <AlertBox
          isOpen={isOpenAlertError}
          setIsOpen={setIsOpenAlertError}
          header={"We're Sorry! Something is Wrong."}
          message={alertErrorMessage}
        />
      )}
    </IonPage>
  );
};

export default Register;
